import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { UseLocateTogglesProps, UseLocateTogglesResult } from '../types/CleaningControls.types';
import { RobotCleaningControlActions } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/robotCleaningControlSlice';
import {
  selectRobotCleaningControlIsBeepOn,
  selectRobotCleaningControlIsBlinkOn,
  selectRobotCleaningControlIsLocateSettingsResolved,
  selectRobotCleaningControlPendingLocateCommand,
  // eslint-disable-next-line max-len
} from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/robotCleaningControlSelectors';
import { LocateStatus } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/utils/constants';
import type { LocateCommandType } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/types/RobotCleaningControl.types';

/**
 * CV50 Beep / Blink toggles, shared by the basic-mode actions and the live map. Each one follows the device's
 * reported setting (which comes with the paused-state subscription), and a press asks for the opposite state.
 */
export const useLocateToggles = ({ materialNumber, serialNumber }: UseLocateTogglesProps): UseLocateTogglesResult => {
  const dispatch = useDispatch();
  const pendingCommand = useSelector(selectRobotCleaningControlPendingLocateCommand);
  const isBeepOn = useSelector(selectRobotCleaningControlIsBeepOn);
  const isBlinkOn = useSelector(selectRobotCleaningControlIsBlinkOn);
  const isSettingsResolved = useSelector(selectRobotCleaningControlIsLocateSettingsResolved);

  // Drop an in-flight Beep / Blink when the robot changes or the screen closes, so its pending state doesn't
  // carry over to the next robot.
  useEffect(
    () => (): void => {
      dispatch(RobotCleaningControlActions.sendCleaningCommandReset());
    },
    [dispatch, materialNumber, serialNumber]
  );

  const toggle = useCallback(
    (commandType: LocateCommandType, isOn: boolean): void => {
      dispatch(
        RobotCleaningControlActions.sendLocateCommandRequest({
          materialNumber,
          serialNumber,
          commandType,
          status: isOn ? LocateStatus.Off : LocateStatus.On,
        })
      );
    },
    [dispatch, materialNumber, serialNumber]
  );

  return {
    isBeepOn,
    isBlinkOn,
    pendingCommand,
    // Locked until the device has reported Beep / Blink, so a press can't act on a guessed state.
    isLocked: !!pendingCommand || !isSettingsResolved,
    toggle,
  };
};
